// A visitor who signs up on the questions screen gets their first set saved to the account (claimed, its progress
// moved), even when they leave the first review before that save has finished (it retries for as long as the tab is
// open: answerFirstReviewOnTheReviewPage.ts).
// On every page of the app, whenever an account is signed in on this tab (a reload, a page opened from home, the next
// sign-in), a save this tab still holds for that account is taken up again.
//
// In the app's startup (App.tsx), so it is tiny: it reads two stores, and loads the save itself only when there is
// one to finish.

import type { DeckObject } from "../../../../constants/GlobalTypesAndEnumsAndConstants";
import { userStore } from "../../../../zustand/globalStore";
import { useAnswerFirstReviewStore } from "./answerFirstReview";

let accountIdLastLookedAt: string | null = null;

function finishTheSaveOfTheAccountSignedInIfAny(): void {
  const signedInAccount = userStore.getState().currentUser;
  const signedInAccountId = signedInAccount?.id ?? null;
  // Once per account that signs in (the user store changes far more often than that)
  if (signedInAccountId === accountIdLastLookedAt) return;
  accountIdLastLookedAt = signedInAccountId;
  const { answerFirstReview } = useAnswerFirstReviewStore.getState();
  const isASaveOfThisAccountUnfinished =
    Boolean(signedInAccountId) &&
    answerFirstReview?.answersGivenToAccountId === signedInAccountId &&
    !answerFirstReview.answersSavedToAccountAtMilliseconds;
  if (!isASaveOfThisAccountUnfinished) return;
  import("./answerFirstReviewOnTheReviewPage")
    .then(({ saveAnswerFirstAnswersToTheAccount }) =>
      saveAnswerFirstAnswersToTheAccount({
        user: signedInAccount,
        // The set's id is all the save needs (claiming a set that has its owner does nothing)
        deck: { id: answerFirstReview.deckId } as DeckObject,
      })
    )
    .catch((error) => console.error("Could not load the save of the first set to the account:", error));
}

/** Called once at the app's start. */
export function finishAnswerFirstSavesWhenTheirAccountIsSignedIn(): void {
  finishTheSaveOfTheAccountSignedInIfAny();
  userStore.subscribe(finishTheSaveOfTheAccountSignedInIfAny);
}
